import api from '@Services/api';
import { AxiosResponse } from 'axios';
import {
	CRUDBase,
	DeleteSingleEntityPayload,
	GenerateCardMeta,
	GenerateCodesMeta,
	GetAllMeta,
	GetSingleEntityPayload,
} from 'src/base/services/services.base';
import { AttendanceCheckinRow } from '../Types/AttendanceReport.types';

interface AttendanceReportCRUD
	extends CRUDBase<AttendanceCheckinRow, AttendanceCheckinRow> {
	send: (payload: {
		course_id: number;
		group_id?: number | null;
	}) => Promise<
		AxiosResponse<ApiResponse<{ queued: number; recipients: string[] }>>
	>;
	resendNotification: (payload: {
		course_id: number;
		checkin_id: number;
		recipient: 'student' | 'parent';
	}) => Promise<AxiosResponse<ApiResponse<{ id: number; status: string }>>>;
}

/**
 * Reports → Attendance tab. The slice hands `id` (the lecture's course id)
 * like every report requester; the backend wants `course_id`, mapped here.
 */
export class AttendanceReportRequester implements AttendanceReportCRUD {
	async export(payload: GetAllMeta): Promise<AxiosResponse<string>> {
		return await api.post(`${this.baseURL}/attendance/single`, {
			...payload,
			course_id: Number((payload as any).id),
			export: true,
		});
	}
	generate?(
		payload: GenerateCodesMeta | GenerateCardMeta
	): Promise<AxiosResponse<string>> {
		throw new Error('Method not implemented.');
	}
	toggleActive?(
		payload: AttendanceCheckinRow
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>> {
		throw new Error('Method not implemented.');
	}
	process?(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
		throw new Error('Method not implemented.');
	}
	get(id: string): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>>;
	get(id: number): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>>;
	get(
		id: string | number,
		tId?: string | number | undefined,
		payload?: GetSingleEntityPayload | undefined
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>>;
	get(
		id: unknown,
		tId?: unknown,
		payload?: unknown
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>> {
		throw new Error('Method not implemented.');
	}
	delete(id: number): Promise<AxiosResponse<ApiResponse<any>>>;
	delete(id: string): Promise<AxiosResponse<ApiResponse<any>>>;
	delete(
		id: string | number,
		payload?: DeleteSingleEntityPayload | undefined
	): Promise<AxiosResponse<ApiResponse<any>>>;
	delete(
		id: unknown,
		payload?: unknown
	): Promise<AxiosResponse<ApiResponse<any>>> {
		throw new Error('Method not implemented.');
	}
	create(
		payload: Omit<AttendanceCheckinRow, 'id'>
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>> {
		throw new Error('Method not implemented.');
	}
	update(
		payload: AttendanceCheckinRow
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow>>> {
		throw new Error('Method not implemented.');
	}
	private static instance: AttendanceReportRequester;
	static getInstance() {
		if (!AttendanceReportRequester.instance) {
			AttendanceReportRequester.instance = new AttendanceReportRequester();
		}
		return AttendanceReportRequester.instance;
	}
	baseURL = 'reports/course';
	getAll(
		payload?: GetAllMeta
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow[]>>>;
	getAll(
		payload: number
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow[]>>>;
	async getAll(
		payload?: any
	): Promise<AxiosResponse<ApiResponse<AttendanceCheckinRow[]>>> {
		return await api.post(`${this.baseURL}/attendance/single`, {
			...payload,
			course_id: Number(payload?.id),
		});
	}

	/** Queue every UNSENT parent message of the lecture (never repeats a sent one). */
	async send(payload: { course_id: number; group_id?: number | null }) {
		return await api.post<ApiResponse<{ queued: number; recipients: string[] }>>(
			`${this.baseURL}/attendance/notification/send`,
			payload
		);
	}

	/** Per-row retry — the only way an existing message is sent again. */
	async resendNotification(payload: {
		course_id: number;
		checkin_id: number;
		recipient: 'student' | 'parent';
	}) {
		return await api.post<ApiResponse<{ id: number; status: string }>>(
			`${this.baseURL}/attendance/notification/resend`,
			payload
		);
	}
}
